"use client";

import Translations from "@translate/screens/DocsDetailArticle";
import { DocsMarkdown } from "@components/DocsMarkdown";
import { News } from "@components/TokenDetails/News";
import { ShellLink } from "@components/ShellNavigation/ShellAwareLink";
import {
  ContentCategoryEnum,
  OpenDataTypeEnum,
  type Content,
} from "@features/api/openData";
import React, { PropsWithChildren } from "react";
import { dayjs, formatTitleForUrl, transformIpfs, cn } from "utils";
import { DeviceType } from "utils/src/uaDevicesParse/ua-device-parser";
import { DocsHeader } from "@components/AIInsight/DocsHeader";
import type { DocsDetailPageServersData } from "@screens/types";
import { I18nUtil } from "@utils/i18n";
import { Locale } from "@utils/i18n.locale";
import { ROUTER_PATH } from "@config/router";
import { StockMarketType } from "@const/market";
import { ThemeWithMarketData } from "@componentsv1/themes/type";
import { Tooltip } from "antd";
import { buildLocale } from "@utils/buildLocale";
import { buildNewsThemeSubTagHref } from "@hooks/useHotNews.helper";
import { ospThemes } from "@ospweb/sdk-ui";
import { useAIInsightDetail } from "@hooks/useAIInsightDetail";
import { useHotNews, type ContentWithThemeRanking } from "@hooks/useHotNews";
import type { AIInsightContentTabType } from "@components/AIInsight/types";
import Image from "next/image";

interface RecommendTooltipProps {
  children: React.ReactNode;
  title: string;
}

const RecommendTooltip = ({
  children,
  title,
}: PropsWithChildren<RecommendTooltipProps>) => {
  return (
    <Tooltip
      title={<span className="body_s text-content_secondary">{title}</span>}
      arrow={false}
      color={ospThemes.colors.background_solid_tertiary}
      placement="bottomRight"
      classNames={{
        root: "max-w-[300px] rounded-2 border border-border_white_transparent10",
      }}
    >
      {children}
    </Tooltip>
  );
};

export type DocsDetailArticleProps = {
  serverData: DocsDetailPageServersData;
  locale: Locale;
  category: string;
  contentType: AIInsightContentTabType;
  deviceType: DeviceType;
  /**
   * embed：序列内嵌 — 无侧栏；相关新闻取热榜前 relatedLimit 条（默认 3）。
   * full：Docs 全页 — 侧栏 4 + 底部 4–7（现网）。
   */
  mode?: "full" | "embed";
  relatedLimit?: number;
  relatedSectionTitle?: string;
  className?: string;
};

/**
 * Docs 详情正文区（Header + Markdown + 来源 + 相关新闻）。
 * 全页与 Brief 序列 NewsEmbed 共用，禁止 iframe 套壳。
 */
export function DocsDetailArticle({
  serverData,
  category,
  contentType,
  locale,
  deviceType: deviceTypeServer,
  mode = "full",
  relatedLimit = 3,
  relatedSectionTitle,
  className,
}: DocsDetailArticleProps) {
  const deviceType = deviceTypeServer === "desktop" ? "desktop" : "mobile";
  const I18n = I18nUtil.createScoped(Translations, locale);
  const embed = mode === "embed";

  const {
    details: {
      Title,
      Content,
      publishedAt,
      Author,
      Analyst,
      HeadImg,
      References = [],
      Themes,
      Summary,
      EnTitle,
    },
    priceChanges,
  } = serverData || {};
  const categoryParmas =
    category === "crypto"
      ? ContentCategoryEnum.CRYPTO
      : ContentCategoryEnum.STOCK;

  const { data } = useHotNews({
    initialParams: { category: categoryParmas, locale },
  });
  const filteredRecommendData =
    data?.filter((article) => article.title !== Title) ?? [];

  const asideRecommend: ContentWithThemeRanking[] = embed
    ? []
    : filteredRecommendData.slice(0, 4);
  const bottomRecommend = embed
    ? filteredRecommendData.slice(0, relatedLimit)
    : filteredRecommendData.slice(4, 7);

  const isCrypto = category === "crypto";
  const { data: themeList } = useAIInsightDetail({
    themeId: Themes?.[0]?.ThemeId || "",
    type: isCrypto ? OpenDataTypeEnum.TOKEN : OpenDataTypeEnum.STOCK,
    locale,
  });
  const theme = (themeList?.[0] ?? null) as ThemeWithMarketData | null;
  const type = isCrypto
    ? "crypto"
    : theme?.market === StockMarketType.US
      ? "us-stocks"
      : "hk-stocks";

  const relatedTitle =
    relatedSectionTitle ??
    (embed ? "News about this signal" : I18n.t("Recommend"));

  return (
    <div
      className={cn(
        `md:max-w-[1165px] w-full flex flex-row mt-[6px] relative ${deviceType === "mobile" && "justify-center"}`,
        className,
      )}
    >
      <div
        className={` w-full flex flex-col items-center px-2 md:px-8 ${deviceType !== "mobile" ? "max-w-[912px] min-w-[630px]" : "max-w-[393px]"}`}
      >
        <DocsHeader
          title={Title || ""}
          category={type}
          contentType={contentType}
          tokens={priceChanges}
          author={{
            logo: "/assets/images/ai-insight-docs-avatar.svg",
            name: Author,
            time: publishedAt || "",
          }}
          analyst={Analyst}
          headImage={HeadImg || ""}
          locale={locale}
          deviceType={deviceTypeServer}
          theme={theme as ThemeWithMarketData}
          embed={embed}
          newsThemeItem={
            Themes?.[0]?.themeItem
              ? {
                  ...Themes[0].themeItem,
                  theme:
                    Themes[0].themeItem.theme ?? Themes[0].Name ?? undefined,
                }
              : undefined
          }
          summary={Summary || ""}
          enTitle={EnTitle || ""}
        />
        <main
          aria-label="Recommend Tooltip main"
          data-openreplay-label="misc_recommendtooltip_main"
          id="sem-recommend-tooltip-main"
          className={`flex flex-col pb-6 mt-12 w-full min-w-0 ${deviceType === "mobile" ? "min-w-[361px]" : ""}`}
        >
          {Content && <DocsMarkdown content={Content} />}
          {References?.length > 0 && (
            <section
              aria-label="Recommend Tooltip section"
              data-openreplay-label="misc_recommendtooltip_section"
              id="sem-recommend-tooltip-section"
              className="flex flex-col mt-8 text-content_tertiary body_m mb-4"
            >
              <span className="h-fit text-content_tertiary body_m mb-2">
                {I18n.t("source:")}
              </span>
              <div className="flex flex-col gap-6 w-fit">
                {References.map((reference, index) => (
                  <ShellLink
                    aria-label="Recommend Tooltip"
                    data-openreplay-label="misc_recommendtooltip_open"
                    id="lnk-recommend-tooltip"
                    key={reference.id}
                    href={reference?.Url || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="break-all body_s text-content_secondary hover:text-content_brand_primary cursor-pointer"
                  >
                    {`[${index + 1}] ${reference.Name}`}
                  </ShellLink>
                ))}
              </div>
            </section>
          )}

          {bottomRecommend.length > 0 ? (
            <section
              aria-label="Recommend Tooltip section"
              data-openreplay-label="misc_recommendtooltip_section"
              id="sem-recommend-tooltip-section-2"
              className="flex flex-col gap-4 mt-4 text-content_primary title_l mb-0 md:mb-24"
            >
              {deviceType !== "mobile" || embed ? (
                <>
                  <span>{relatedTitle}</span>
                  <div className="flex flex-col gap-6">
                    {bottomRecommend.map((item) => {
                      const urlPrefix = buildLocale(locale);
                      const detailUrl =
                        `${urlPrefix}${ROUTER_PATH.NEWS_TITLE_DETAIL}/${formatTitleForUrl(item?.source_link || "")}` ||
                        "";
                      return (
                        <ShellLink
                          aria-label="Recommend Tooltip"
                          data-openreplay-label={
                            embed
                              ? "brief_sequence_related_news"
                              : "misc_recommendtooltip_related"
                          }
                          id="lnk-recommend-tooltip-2"
                          key={item.id}
                          href={detailUrl}
                          className="flex flex-col gap-2"
                        >
                          <RecommendTooltip title={item.title ?? ""}>
                            <span className="title_l text-content_primary hover:text-content_secondary">
                              {item.title}
                            </span>
                          </RecommendTooltip>
                          <span className="text-content_tertiary body_s">
                            {dayjs(item.issue_date).format(
                              "MMM DD YYYY, HH:mm",
                            )}
                          </span>
                        </ShellLink>
                      );
                    })}
                  </div>
                </>
              ) : (
                <News
                  news={bottomRecommend as Content[]}
                  locale={locale}
                  category={category}
                />
              )}
            </section>
          ) : null}
        </main>
      </div>
      {!embed && asideRecommend.length > 0 && deviceType !== "mobile" && (
        <div className="flex-shrink-0 max-w-[312px] w-full">
          <aside
            aria-label="Recommend Tooltip aside"
            data-openreplay-label="misc_recommendtooltip_aside"
            id="sem-recommend-tooltip-aside"
            className="sticky top-4 flex flex-col"
          >
            <h2
              aria-label="Recommend Tooltip h2"
              data-openreplay-label="misc_recommendtooltip_h2"
              id="sem-recommend-tooltip-h2"
              className="title_l text-content_primary my-4 "
            >
              {I18n.t("Hot News")}
            </h2>
            {asideRecommend.map((item) => {
              const urlPrefix = buildLocale(locale);
              const detailUrl =
                `${urlPrefix}${ROUTER_PATH.NEWS_TITLE_DETAIL}/${formatTitleForUrl(item?.source_link || "")}` ||
                "";
              const themeHref = buildNewsThemeSubTagHref(item, locale);
              const themeRow =
                item.themeRowsWithRanking?.find((r) => r != null) ?? undefined;
              const themeLabel =
                themeRow?.theme ??
                themeRow?.slug ??
                item.themes?.[0] ??
                undefined;
              const themeLineClassName =
                "text-content_brand_primary body_medium_s w-full line-clamp-1 z-20 mt-3";
              return (
                <div
                  key={item.id}
                  className="flex flex-col pb-2 mb-8 relative w-[252px] h-fit items-center justify-end overflow-hidden"
                >
                  <ShellLink
                    aria-label="Recommend Tooltip"
                    data-openreplay-label="misc_recommendtooltip_open"
                    id="lnk-recommend-tooltip-3"
                    href={detailUrl}
                    className="flex flex-col w-full cursor-pointer"
                  >
                    <Image
                      className="w-full h-full object-cover rounded-2 "
                      src={transformIpfs(item.medias?.[0]?.url || "")}
                      alt={item.title || ""}
                      width={252}
                      height={142}
                    />
                  </ShellLink>
                  {themeLabel && themeHref ? (
                    <ShellLink
                      aria-label="Recommend Tooltip"
                      data-openreplay-label="misc_recommendtooltip_open"
                      id="lnk-recommend-tooltip-4"
                      href={themeHref}
                      className={themeLineClassName}
                    >
                      {themeLabel}
                    </ShellLink>
                  ) : themeLabel ? (
                    <span className={themeLineClassName}>{themeLabel}</span>
                  ) : null}
                  <ShellLink
                    title={item.title}
                    aria-label={item.title}
                    data-openreplay-label="misc_docsdetailpage_open"
                    id="lnk-recommend-tooltip-5"
                    href={detailUrl}
                    className="flex flex-col w-full cursor-pointer"
                  >
                    <span className="text-content_primary body_medium_l w-full line-clamp-3 z-20 mt-1">
                      {item.title}
                    </span>
                    <span className="text-content_tertiary body_s w-full line-clamp-1 z-20 mt-1">
                      {dayjs(item.issue_date).format("MMM DD YYYY, HH:mm")}
                    </span>
                  </ShellLink>
                </div>
              );
            })}
          </aside>
        </div>
      )}
    </div>
  );
}
